import { KboAgentInvocation } from "./KboFlowRunDebug";

/**
 * Client-side pricing used to *estimate* the dollar cost of a KBo flow run from the
 * token counts recorded on each agent invocation. Prices are USD per 1,000,000 tokens
 * and mirror the public OpenAI / Azure OpenAI list pricing for the models KBo can be
 * configured to use (models are selected per-step in the workflow configuration, so a
 * single run may span several of these). Update these numbers if the underlying pricing
 * changes — the run debug view is the only consumer.
 */
export interface ModelPricing {
    /** USD per 1M *uncached* input (prompt) tokens. */
    input: number;
    /** USD per 1M cached input (prompt-cache) tokens. */
    cachedInput: number;
    /** USD per 1M output (completion) tokens. */
    output: number;
}

/**
 * Pricing keyed by a lowercase base-model fragment. Lookup matches by substring, so
 * custom deployment names (e.g. `gpt-4.1-kbo-only`) resolve to their base model.
 * Order matters: more specific fragments must come before their prefixes so that,
 * for example, `gpt-4.1-mini` is matched before `gpt-4.1`.
 */
const MODEL_PRICING: ReadonlyArray<{
    fragment: string;
    pricing: ModelPricing;
}> = [
    { fragment: "gpt-5.6-luna", pricing: { input: 0.2, cachedInput: 0.02, output: 1.2 } },
    { fragment: "gpt-4.1-nano", pricing: { input: 0.1, cachedInput: 0.025, output: 0.4 } },
    { fragment: "gpt-4.1-mini", pricing: { input: 0.4, cachedInput: 0.1, output: 1.6 } },
    { fragment: "gpt-4.1", pricing: { input: 2.0, cachedInput: 0.5, output: 8.0 } },
    { fragment: "gpt-4o-mini", pricing: { input: 0.15, cachedInput: 0.075, output: 0.6 } },
    { fragment: "gpt-4o", pricing: { input: 2.5, cachedInput: 1.25, output: 10.0 } },
];

/** Resolve pricing for a model/deployment name, or null when it isn't recognized. */
export function pricingForModel(model?: string | null): ModelPricing | null {
    if (!model) return null;
    const normalized = model.toLowerCase();
    return (
        MODEL_PRICING.find((entry) => normalized.includes(entry.fragment))
            ?.pricing ?? null
    );
}

export interface CostEstimate {
    /** Total estimated cost in USD across all priced invocations. */
    totalUsd: number;
    /** True when at least one invocation had a priced model and non-zero tokens. */
    hasPricedTokens: boolean;
    /** Distinct model names that carried tokens but could not be priced. */
    unpricedModels: string[];
}

/**
 * Estimate the total USD cost of a set of invocations. Cached input tokens are billed
 * at the discounted cached rate; the remaining (uncached) input tokens at the full
 * input rate. Invocations whose model can't be matched are counted as unpriced so the
 * UI can flag that the estimate is incomplete.
 */
export function estimateInvocationsCost(
    invocations: ReadonlyArray<KboAgentInvocation>,
): CostEstimate {
    let totalUsd = 0;
    let hasPricedTokens = false;
    const unpriced = new Set<string>();

    for (const invocation of invocations) {
        const inputTokens = invocation.inputTokens ?? 0;
        const cachedInputTokens = Math.min(
            invocation.cachedInputTokens ?? 0,
            inputTokens,
        );
        const uncachedInputTokens = inputTokens - cachedInputTokens;
        const outputTokens = invocation.outputTokens ?? 0;
        const tokens = uncachedInputTokens + cachedInputTokens + outputTokens;
        if (tokens === 0) continue;

        const model = invocation.model || invocation.deployment;
        const pricing = pricingForModel(model);
        if (!pricing) {
            unpriced.add(model || "unknown");
            continue;
        }

        totalUsd +=
            (uncachedInputTokens / 1_000_000) * pricing.input +
            (cachedInputTokens / 1_000_000) * pricing.cachedInput +
            (outputTokens / 1_000_000) * pricing.output;
        hasPricedTokens = true;
    }

    return {
        totalUsd,
        hasPricedTokens,
        unpricedModels: [...unpriced],
    };
}

/** Format a USD amount with cent precision, or sub-cent precision for tiny amounts. */
export function formatUsd(amount: number): string {
    if (amount > 0 && amount < 0.01) {
        return `$${amount.toFixed(4)}`;
    }
    return amount.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
    });
}
